'use client';

import clsx from 'clsx';
import Link from 'next/link';

import {ArrowLink} from '@/modules/finkn/components/arrow-link';
import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import {GridList} from '@/modules/foundation/components/grid-list';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {KeyedArray} from '@/modules/foundation/shared/types';
import {
	ContentBlockContainer,
	type ContentBlockContainerProps,
} from '@/modules/page/components/content-blocks/block-container';
import {type FinknIcons, HoverIcon} from '@/shared/renderIcon';

type AreaDetails = {
	title: string;
	href: string;
};

export type FieldOfAreaDetails = {
	title: string;
	href: string;
	areas?: KeyedArray<AreaDetails>;
	areasHasMore?: boolean;
	icon?: FinknIcons;
};

interface Props extends ContentBlockContainerProps {
	fieldOfAreas: KeyedArray<FieldOfAreaDetails>;
	heading?: string;
	anchorHeadings?: boolean;
	insideToc?: boolean;
	showDetails?: boolean;
}

const FieldOfAreaList = ({
	fieldOfAreas,
	heading,
	anchorHeadings,
	options,
	insideToc,
	showDetails,
}: Props) => {
	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<ContentBlockHeading anchorHeading={anchorHeadings} heading={heading} />

				<GridList>
					{fieldOfAreas.map((fieldOfArea) => {
						if (!showDetails) {
							return (
								<li key={fieldOfArea._key}>
									<Link
										href={fieldOfArea.href}
										className={clsx(
											'block',
											'bg-white',
											'shadow-card',
											'p-6',
											'group',
											'rounded-[3px]',
										)}
									>
										<HoverIcon icon={fieldOfArea.icon} className="mb-8" />
										<Heading
											level={heading ? 3 : 2}
											size="sm"
											// asChild
											className={clsx(
												'flex',
												'items-center',
												'justify-between',
												'gap-4',
												'transition-colors',
												'group-hover:text-primary-15',
											)}
										>
											{fieldOfArea.title}
											<ArrowRightIcon
												aria-hidden
												className={clsx(
													'text-grey-25',
													'text-base',
													'shrink-0',
													'transition-all',
													'group-hover:text-primary-15',
													'mr-2',
													'group-hover:mr-0',
												)}
											/>
										</Heading>
									</Link>
								</li>
							);
						}
						return (
							<li
								key={fieldOfArea._key}
								className={clsx(
									'p-6',
									'grid',
									'gap-4',
									'bg-white',
									'shadow-card',
									'group',
									'rounded-[3px]',
								)}
							>
								<HoverIcon icon={fieldOfArea.icon} />
								<Heading level={heading ? 3 : 2} size="md">
									{fieldOfArea.title}
								</Heading>
								<ul className={clsx('grid', 'gap-3')}>
									{fieldOfArea.areas?.map((area) => (
										<li key={area._key}>
											<Link
												href={area.href}
												className={clsx(
													'text-base',
													'text-primary-15',
													'hover:text-link',
													'transition-colors',
												)}
											>
												{area.title}
											</Link>
										</li>
									))}
								</ul>
								<ArrowLink
									href={fieldOfArea.href}
									className={clsx(
										'text-base',
										'font-medium',
										'border-t',
										'border-grey-border',
										'pt-5',
										'mt-2',
									)}
								>
									Alt om {fieldOfArea.title}
								</ArrowLink>
							</li>
						);
					})}
				</GridList>
			</div>
		</ContentBlockContainer>
	);
};

export default FieldOfAreaList;
